import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useRef, useState } from 'react';

export type PlacementRenderProps = {
	dismiss: VoidFunction;
};

export type AppProps = {
	onVisibilityChange?: (isVisible: boolean) => void;
	transactionAccountId?: string;
	entitlementId?: string;
};

export type PlacementFrontendProps = AppProps;

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'bac-subscription-details-banner',
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

const PlacementVisibilityNotifier = ({
	isVisible,
	onVisibilityChange,
}: {
	isVisible: boolean;
	onVisibilityChange: (isVisible: boolean) => void;
}) => {
	const callbackRef = useRef(onVisibilityChange);
	useEffect(() => {
		callbackRef.current = onVisibilityChange;
	}, [onVisibilityChange]);

	useEffect(() => {
		callbackRef.current(isVisible);
		return () => callbackRef.current(false);
	}, [isVisible]);

	return null;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>(
	(Messages) =>
		({ transactionAccountId, entitlementId, onVisibilityChange }) => {
			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				queryParams: {
					transactionAccountId: transactionAccountId ?? '',
					entitlementId: entitlementId ?? '',
				},
			});
			const [dismissedMessageIds, setDismissedMessageIds] = useState<Set<string>>(() => new Set());

			const message =
				error || isLoading
					? undefined
					: data?.messages.find((msg) => !dismissedMessageIds.has(msg.messageInstanceId));

			return (
				<>
					{onVisibilityChange && (
						<PlacementVisibilityNotifier
							isVisible={Boolean(message)}
							onVisibilityChange={onVisibilityChange}
						/>
					)}
					{message && (
						<Messages.render
							{...message}
							dismiss={() => {
								setDismissedMessageIds((currentDismissedIds) =>
									new Set(currentDismissedIds).add(message.messageInstanceId),
								);
							}}
						/>
					)}
				</>
			);
		},
);
